/* eslint-disable no-console */
import axios from 'axios';

import { getAxiosErrorCode, getAxiosErrorStatus } from '../axios-http';
import { envEngine } from '../environments/environment-game-engine';

/**
 * Polls the platform REST API until `/system-health/match-capable` succeeds.
 * Shared by GES startup and local SP hosts (e.g. pixi); not GES-specific.
 */
export class RestServerDiagnostic {
  private static readonly RETRY_INTERVAL: number = 5000;
  private static readonly MAX_ATTEMPTS: number = 10;

  private static _matchCapable?: boolean = undefined;

  public static get matchCapable(): boolean {
    return !!this._matchCapable;
  }

  /**
   * Checks REST server and retries until successful
   * @param interval Milliseconds between retry attempts (default: 5000)
   * @returns Promise that resolves when server is match-capable
   */
  public static async waitUntilMatchCapable(interval: number = this.RETRY_INTERVAL): Promise<void> {
    let attempt = 0;
    while (!this._matchCapable && attempt < this.MAX_ATTEMPTS) {
      attempt++;
      this._matchCapable = await this.checkMatchCapable();
      if (this._matchCapable) { return; }

      console.warn(`REST server not reachable (attempt ${attempt}), retrying in ${interval}ms...`);
      await new Promise((resolve) => setTimeout(resolve, interval));
    }

    if (!this._matchCapable) {
      console.warn(`Failed to establish REST server connection after ${this.MAX_ATTEMPTS} attempts.`);
    }
  }

  private static async checkMatchCapable(): Promise<boolean> {
    const url = `${envEngine.gameAPIEndpoint}/system-health/match-capable`;
    try {
      const response = await axios.get<boolean>(url);
      return response.data;
    }
    catch (reason: unknown) {
      const code = getAxiosErrorCode(reason);
      if (code) {
        const status = getAxiosErrorStatus(reason);
        const statusText = status ? `, status <${status}>` : '';
        console.error(`REST server checkMatchCapable error: ${code}${statusText}, url <${url}>`);
      }
      else {
        const status = getAxiosErrorStatus(reason);
        const statusText = status ? `status <${status}>` : undefined;
        const message = reason instanceof Error ? reason.message : String(reason);
        console.error(`REST server checkMatchCapable error: ${statusText ?? message}, url <${url}>`);
      }
      return false;
    }
  }
}
